import type { IAdProvider, AdReward, AdRewardType, AdStatus } from './AdProvider.js';

/**
 * Mock Ad Provider for testing and development
 * Simulates ad behavior with configurable delays
 */
export class MockAdProvider implements IAdProvider {
  readonly name = 'MockAdProvider';
  private _status: AdStatus = 'idle';
  private currentReward: AdReward | null = null;
  private loadDelay: number;
  private showDelay: number;
  private successRate: number;

  constructor(options?: { loadDelay?: number; showDelay?: number; successRate?: number }) {
    this.loadDelay = options?.loadDelay ?? 500;
    this.showDelay = options?.showDelay ?? 2000;
    this.successRate = options?.successRate ?? 0.9;
  }

  get status(): AdStatus {
    return this._status;
  }

  async initialize(): Promise<boolean> {
    console.log('[MockAdProvider] Initialized');
    this._status = 'idle';
    return true;
  }

  isRewardedAdReady(): boolean {
    return this._status === 'ready';
  }

  async loadRewardedAd(_rewardType?: AdRewardType): Promise<boolean> {
    if (this._status === 'loading') return false;

    this._status = 'loading';
    return new Promise((resolve) => {
      setTimeout(() => {
        if (Math.random() < this.successRate) {
          this._status = 'ready';
          resolve(true);
        } else {
          this._status = 'error';
          resolve(false);
        }
      }, this.loadDelay);
    });
  }

  async showRewardedAd(_rewardType?: AdRewardType): Promise<AdReward | null> {
    if (this._status !== 'ready') return null;

    this._status = 'showing';
    return new Promise((resolve) => {
      setTimeout(() => {
        if (Math.random() < this.successRate) {
          const reward = this.currentReward;
          this._status = 'idle';
          this.currentReward = null;
          resolve(reward);
        } else {
          this._status = 'idle';
          resolve(null);
        }
      }, this.showDelay);
    });
  }

  setReward(reward: AdReward): void {
    this.currentReward = reward;
  }

  destroy(): void {
    this._status = 'idle';
    this.currentReward = null;
  }
}
